import { ccySym } from '@/utils/currencyUtils';

function sym(trip) {
  return ccySym(trip?.primary_currency) || '€';
}

// Devises sans décimale — doit rester en sync avec ZERO_DEC_CCY dans TripForm/ShipmentForm
export const ZERO_DEC_CCY = new Set(['XOF', 'XAF', 'NGN', 'GHS']);

// Taux de secours (1 EUR = X unités), relevés le 2026-10-09 : valeur de départ avant l'arrivée des taux du jour.
// Copie exacte de STATIC_RATES_EUR du serveur (backend/currency_service.py) — vérifiée par
// backend/tests/unit/test_static_rates_sync.py : modifier les deux ensemble.
export const STATIC_RATES_EUR = {
  EUR: 1, USD: 1.12, GBP: 0.85, CAD: 1.6, CHF: 0.93,
  ZAR: 18.6, CNY: 7.5, AED: 4.12, MAD: 11.1,
  NGN: 1490, GHS: 13.2, XOF: 655.957, XAF: 655.957,
};

/**
 * Calcule les bornes min/max du tarif/kg dans une devise donnée.
 * min = 1 EUR équivalent, max = 250 EUR (particulier) ou 1500 EUR (pro) équivalent.
 * @param {string} currency
 * @param {boolean} isPro
 * @returns {{ min: number, max: number }}
 */
export function calcPriceLimits(currency, isPro) {
  const rate   = STATIC_RATES_EUR[currency] ?? 1;
  const maxEur = isPro ? 1500 : 250;
  const round  = (x) => ZERO_DEC_CCY.has(currency) ? x : Math.round(x * 100) / 100;
  return {
    min: ZERO_DEC_CCY.has(currency) ? Math.ceil(rate) : round(rate),
    max: ZERO_DEC_CCY.has(currency) ? Math.floor(rate * maxEur) : round(rate * maxEur),
  };
}

/**
 * Calcule le budget minimum (côté ShipmentForm) dans une devise donnée.
 * min = 1 EUR équivalent, avec taux de fallback ou taux live.
 * @param {string} currency
 * @param {object} rates — map code→rate (1 EUR = X unités), ex: STATIC_RATES_EUR
 * @returns {number}
 */
export function calcShipmentMinPrice(currency, rates) {
  const r = (rates ?? STATIC_RATES_EUR);
  const rate = r[currency] ?? 1;
  return ZERO_DEC_CCY.has(currency) ? Math.ceil(rate) : Math.round(rate * 100) / 100;
}

/**
 * Returns display info for a trip's price based on its pricing_mode.
 * @param {object} trip
 * @returns {{ amount: string|null, unit: string, label: string, display: string }}
 */
export function tripPriceDisplay(trip) {
  const mode = trip?.pricing_mode || 'kg';
  const s = sym(trip);

  if (mode === 'libre') {
    return { amount: 'Libre', unit: '', label: 'Prix négociable', display: 'Libre' };
  }

  const isZeroDec = ZERO_DEC_CCY.has(trip?.primary_currency);
  const fmtOpts = isZeroDec ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  if (mode === 'fixe') {
    const val = trip?.price_fixed != null ? Number(trip.price_fixed) : null;
    const formatted = val != null ? val.toLocaleString('fr-FR', fmtOpts) : '·';
    return {
      amount: formatted,
      unit: `${s}/colis`,
      label: 'Prix fixe / colis',
      display: `${formatted} ${s}/colis`,
    };
  }

  // kg (default)
  const val = trip?.price_per_kg != null ? Number(trip.price_per_kg) : null;
  const formatted = val != null ? val.toLocaleString('fr-FR', fmtOpts) : '·';
  return {
    amount: formatted,
    unit: `${s}/kg`,
    label: 'Tarif au kilo',
    display: `${formatted} ${s}/kg`,
  };
}
